import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { VALID_SKILL_SLUG_PATTERN } from "../../../../shared/domain/agents/agent-prompt.utils";
import {
  COLLECTION_TOOL_INSTRUCTIONS_REQUIRED_MESSAGE,
  collectionConfigLacksInstructions
} from "../../../../shared/domain/tools/collection-tool-instructions";
import { MemoryToolGrantSchema } from "../../agent-memory/schemas/memory-schema-pad";

/**
 * Whether this config type addresses an `AgentExternalTool` through
 * `config.toolId`, and therefore cannot be stored without one.
 *
 * 🔴 A `switch` WITH NO `default`, SO THE NEXT ENUM MEMBER IS A COMPILE ERROR
 * HERE. Written as `type === PLUGIN` — which is what both refinements below used
 * to spell — a new member silently skips the requirement and a config naming no
 * tool at all is stored at 201. That is the class this whole file's MEMORY arm
 * exists to close, in its other costume: nothing throws, and the skill is simply
 * inert.
 *
 * PLUGIN and MCP are the two, and they are the same claim about storage rather
 * than two similar ones: an MCP server IS an `AgentExternalTool` row, so its
 * config addresses the identical column. Their `AgentToolConfigMcpToolPin` is
 * what distinguishes them afterwards, and a pin is not expressible on these
 * bodies.
 */
function requiresExternalToolId(type: DbEnum.AgentToolConfigType): boolean {
  switch (type) {
    case DbEnum.AgentToolConfigType.PLUGIN:
    case DbEnum.AgentToolConfigType.MCP:
      return true;
    case DbEnum.AgentToolConfigType.WORKFLOW:
    case DbEnum.AgentToolConfigType.TASK:
    case DbEnum.AgentToolConfigType.COLLECTION:
    case DbEnum.AgentToolConfigType.DOCUMENT_TEMPLATE:
    case DbEnum.AgentToolConfigType.MEMORY:
      return false;
  }
}

/**
 * The MEMORY arm of both refinements below, in one place because the two must agree.
 *
 * ## 🔴 WHY THE SERVER HAS TO REFUSE THIS, WHEN NOTHING CRASHES IF IT DOES NOT
 *
 * `config` on these bodies is `z.record(z.string(), z.unknown())`, so before this arm a
 * MEMORY row could be created or replaced with `{}` or `{ pads: [] }`. Nothing throws:
 * `readMemoryToolGrant` falls back to no pads — deliberately, so a row stored before the pad
 * axis existed keeps loading — `resolveMemoryToolAgentInputSchema` derives `{}` from it, and
 * the tool ships advertised with **no parameters**, refusing every call the model makes. An
 * agent silently loses its ability to take notes, with no error on any surface.
 *
 * The read fallback is right and stays. The asymmetry is the contract
 * `MemoryToolGrantSchema`'s own docblock states — lenient on READ, `.min(1)` on WRITE — and
 * this is the write half of it, which had no enforcer.
 *
 * ⚠️ REUSE, NEVER RE-STATE. Re-implementing "has a non-empty `pads` array" here would be a
 * second statement of the grant contract, free to disagree with the one the backend actually
 * derives `agentInputSchema` from — and it would not check pad NAME shape at all, so a grant
 * naming a pad no `MemorySchema.name` can ever hold would still be accepted and still derive
 * `{}`. `MemorySchemaPadNameSchema` is the only thing that decides what a pad may be called.
 *
 * The one issue mirrors the PLUGIN arm's shape rather than forwarding the grant schema's own
 * issues, so every arm on this body reads the same way.
 */
const addMemoryGrantIssue = (config: unknown, ctx: z.RefinementCtx): void => {
  if (MemoryToolGrantSchema.safeParse(config).success) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["config", "pads"],
    message:
      "MEMORY config requires `pads`: a non-empty array of memory pad names, each starting " +
      "with a lowercase letter and made of lowercase letters, digits and underscores. A " +
      "MEMORY tool granting no pads advertises no parameters and can write nothing."
  });
};

/**
 * Lifecycle status of the skill a config points at (workflow/task), resolved at
 * read time. Drives the broken-skill badge + toggle lock on the agent Skills
 * page. `PUBLISHED` means healthy/usable (workflows: published; tasks: exists).
 * `null` for tool types that aren't gated (PLUGIN / COLLECTION / DOCUMENT_TEMPLATE).
 */
export const UnderlyingSkillStatusSchema = z.enum([
  "PUBLISHED",
  "DRAFT",
  "PAUSED",
  "ARCHIVED",
  "DELETED"
]);

export type UnderlyingSkillStatus = z.infer<typeof UnderlyingSkillStatusSchema>;

/**
 * The identity of the MCP tool a skill is pinned to, as the attached-skill card
 * needs it.
 *
 * What server it came from — by id AND by name — what the tool is called there,
 * and the contract hash it was attached against. The pinned hash is included
 * because a card that can say "this tool's contract has changed since it was
 * approved" is the difference between a skill that silently stops matching and
 * one an operator can act on.
 *
 * 🔴 `serverName` IS CARRIED BECAUSE A CLIENT CANNOT RESOLVE IT, AND THE REASON
 * IS AUTHORIZATION RATHER THAN CONVENIENCE. `serverId` names an
 * `AgentExternalTool` row and the only endpoint that turns one into a name is
 * `GET /api/mcp-servers`, whose controller carries `@UseGuards(OrgAdminGuard)`
 * for every verb on it. The attached-skill card lives on the agent Skills page,
 * which is NOT admin-only — so a client-side join would 403 for every non-admin
 * editing an agent, and the card would fall back to a nameless MCP badge with
 * nothing failing and nothing logged. It would read as correct to whoever built
 * it, because whoever built it is an admin.
 *
 * It is derived by `mcpServerDisplayName`, so it is the same string the MCP
 * server list shows for that row rather than a second opinion about what a
 * nameless server is called.
 */
export const McpToolPinViewSchema = z.object({
  /** `McpServerTool.serverId` — the `AgentExternalTool` id of the MCP server. */
  serverId: z.string(),
  /**
   * The server's display name — `mcpServerDisplayName` over
   * `AgentExternalTool.privateName` and `McpServer.url`. Never empty-by-absence:
   * the URL is the floor.
   */
  serverName: z.string(),
  /** `McpServerTool.name` — the server's own identity for the tool. */
  toolName: z.string(),
  /** `McpServerTool.approvedSchemaHash` at the moment of attaching. */
  pinnedSchemaHash: z.string()
});

export type McpToolPinView = z.infer<typeof McpToolPinViewSchema>;

/**
 * Response shape for a single AgentToolConfig.
 *
 * Mirrors the Prisma AgentToolConfig model fields.
 * agentInputSchema and config are opaque JSON blobs — typed as unknown.
 */
export const AgentToolResponseSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  iconUrl: z.string().nullable(),
  iconType: z.string().nullable(),
  type: z.nativeEnum(DbEnum.AgentToolConfigType),
  agentInputSchema: z.unknown(),
  isActive: z.boolean(),
  fireAndForget: z.boolean(),
  config: z.unknown(),
  agentId: z.string(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Optional + nullable so a missing/null value (non-gated types, or older
  // payloads) never throws at the client `.parse()` boundary.
  underlyingSkillStatus: UnderlyingSkillStatusSchema.nullable().optional(),
  /**
   * The MCP tool this skill is pinned to, or `null` when it is not an MCP skill.
   *
   * 🔴 THIS IS THE ONLY DISCRIMINATOR ON THIS RESPONSE, AND `config` CANNOT
   * SUBSTITUTE FOR IT. An attached MCP tool is stored as `type: PLUGIN` with
   * `config.toolId` set to the MCP server's `AgentExternalTool` id and
   * `config.action` set to the tool's name — and a Pipedream plugin config
   * carries an `action` too, so the two are indistinguishable by shape.
   * `underlyingSkillStatus` is `null` for both, because PLUGIN is not a gated
   * type. The `AgentToolConfigMcpToolPin` row is the only thing in the database
   * that tells them apart, which is why it is surfaced rather than inferred.
   *
   * ⚠️ DO NOT DERIVE THIS FROM `config.action`. It reads like a cheap shortcut
   * and it is wrong for the reason above: a plugin action and an MCP tool name
   * occupy the same key.
   *
   * It carries `serverId` because the card's job is to say WHERE the tool came
   * from, and the pin alone cannot — `serverId` and `toolName` live on
   * `McpServerTool`, one hop past the pin row, so this is resolved through a
   * join rather than read off the pin.
   *
   * Optional + nullable for the same reason as the field above: every payload
   * predating this field, and every non-MCP skill, must parse cleanly.
   */
  mcpToolPin: McpToolPinViewSchema.nullable().optional()
});

export type AgentToolResponse = z.infer<typeof AgentToolResponseSchema>;

/**
 * The COLLECTION arm of both refinements below — see
 * `collection-tool-instructions.ts` for why the field is required and what blank means.
 */
function addCollectionInstructionsIssue(
  type: DbEnum.AgentToolConfigType,
  config: Record<string, unknown>,
  ctx: z.RefinementCtx
): void {
  if (!collectionConfigLacksInstructions(type, config)) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["config", "instructions"],
    message: COLLECTION_TOOL_INSTRUCTIONS_REQUIRED_MESSAGE
  });
}

/**
 * Body schema for creating a tool configuration.
 * agentId comes from the :agentId URL param, not the body.
 */
export const CreateAgentToolBodySchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1)
      .max(64)
      .regex(
        VALID_SKILL_SLUG_PATTERN,
        "Label must be a slug: lowercase letters, digits, and single underscores (e.g. send_invoice, 365_calendar)"
      ),
    description: z.string().optional(),
    iconUrl: z.string().optional(),
    iconType: z.string().optional(),
    type: z.nativeEnum(DbEnum.AgentToolConfigType),
    agentInputSchema: z.record(z.string(), z.unknown()),
    config: z.record(z.string(), z.unknown()),
    isActive: z.boolean().optional(),
    fireAndForget: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    // MCP is held to the SAME requirement as PLUGIN, because it names the same
    // thing: an MCP config's `config.toolId` is an ordinary `AgentExternalTool`
    // id — the MCP server — and the pin names the tool within it. A config with
    // no `toolId` therefore names no attachable server, exactly as a PLUGIN with
    // none names no attachable tool. Nothing writes MCP yet; this is correct
    // BEFORE a writer exists rather than after, which is the whole point of
    // landing the enum label ahead of its producers.
    if (requiresExternalToolId(data.type) && !data.config.toolId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["config", "toolId"],
        message: `${data.type} config requires toolId`
      });
    }
    if (data.type === DbEnum.AgentToolConfigType.MEMORY) {
      addMemoryGrantIssue(data.config, ctx);
    }
    addCollectionInstructionsIssue(data.type, data.config, ctx);
  });

export type CreateAgentToolBody = z.infer<typeof CreateAgentToolBodySchema>;

/**
 * Body schema for updating a tool configuration.
 *
 * `config` updates are wholesale replace (Prisma JSON columns don't deep-merge).
 * To validate shape against the type, we require `type` to accompany any `config`
 * update — the dashboard frontend already does this; CLI/SDK consumers must too.
 */
export const UpdateAgentToolBodySchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1)
      .max(64)
      .regex(
        VALID_SKILL_SLUG_PATTERN,
        "Label must be a slug: lowercase letters, digits, and single underscores (e.g. send_invoice, 365_calendar)"
      )
      .optional(),
    description: z.string().optional(),
    iconUrl: z.string().optional(),
    iconType: z.string().optional(),
    type: z.nativeEnum(DbEnum.AgentToolConfigType).optional(),
    agentInputSchema: z.record(z.string(), z.unknown()).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    isActive: z.boolean().optional(),
    fireAndForget: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    if (data.config !== undefined && data.type === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["type"],
        message: "Must include `type` when updating `config` (wholesale replace semantics)"
      });
    }
    if (
      data.type !== undefined &&
      requiresExternalToolId(data.type) &&
      data.config !== undefined &&
      !data.config.toolId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["config", "toolId"],
        message: `${data.type} config requires toolId`
      });
    }
    // Gated on `config !== undefined` like the PLUGIN arm above: this judges the config a
    // caller is WRITING, never the one already stored. A row whose stored grant is empty
    // stays fully editable — its label, its `isActive`, everything but a second empty grant.
    if (data.type === DbEnum.AgentToolConfigType.MEMORY && data.config !== undefined) {
      addMemoryGrantIssue(data.config, ctx);
    }
    // Same gate: a replaced COLLECTION config is judged, a stored one never is, so a
    // row saved before instructions were required stays editable on every other field.
    if (data.type !== undefined && data.config !== undefined) {
      addCollectionInstructionsIssue(data.type, data.config, ctx);
    }
  });

export type UpdateAgentToolBody = z.infer<typeof UpdateAgentToolBodySchema>;
